import { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { supabase } from '../../../lib/supabase';

export const useOrders = () => {
  const queryClient = useQueryClient();

  // Get all orders
  const { data: orders = [], isLoading, error } = useQuery({
    queryKey: ['orders'],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('orders')
        .select(`
          *,
          customers(name, phone, email),
          products(model_name, category, vendor_id),
          vendors(name)
        `)
        .order('created_at', { ascending: false });
      
      if (error) throw error;
      return data || [];
    }
  });

  // Update order status
  const updateOrderStatus = useMutation({
    mutationFn: async (orderId: string, status: string, notes?: string) => {
      const { data, error } = await supabase
        .from('orders')
        .update({ 
          status,
          updated_at: new Date().toISOString(),
          ...(notes && { notes })
        })
        .eq('id', orderId)
        .select()
        .single();
      
      if (error) throw error;
      return data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['orders'] });
    }
  });

  // Download finished product from vendor
  const downloadFinishedProduct = useMutation({
    mutationFn: async (orderId: string) => {
      // Simulate downloading product from vendor
      const { data, error } = await supabase
        .from('orders')
        .update({ 
          downloaded_at: new Date().toISOString(),
          status: 'received_ho'
        })
        .eq('id', orderId)
        .select()
        .single();
      
      if (error) throw error;
      return data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['orders'] });
    }
  });

  // Transfer product to branch
  const transferToBranch = useMutation({
    mutationFn: async (orderId: string, transferDetails: {
      branchId: string;
      trackingNumber: string;
      courierService: string;
    }) => {
      const { data, error } = await supabase
        .from('orders')
        .update({ 
          branch_id: transferDetails.branchId,
          tracking_number: transferDetails.trackingNumber,
          courier_service: transferDetails.courierService,
          transferred_at: new Date().toISOString(),
          status: 'transferred_to_branch'
        })
        .eq('id', orderId)
        .select()
        .single();
      
      if (error) throw error;
      return data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['orders'] });
    }
  });

  // Notify customer
  const notifyCustomer = useMutation({
    mutationFn: async (customerId: string, notificationType: string, data: any) => {
      // Create notification record
      const { error } = await supabase
        .from('notifications')
        .insert({
          customer_id: customerId,
          type: notificationType,
          message: generateNotificationMessage(notificationType, data),
          data: data,
          sent_at: new Date().toISOString()
        });
      
      if (error) throw error;
      
      // In a real implementation, this would send SMS/email
      console.log(`Notification sent to customer ${customerId}:`, notificationType, data);
      
      return { success: true };
    }
  });

  // Helper function to generate notification messages
  const generateNotificationMessage = (type: string, data: any) => {
    switch (type) {
      case 'product_shipped':
        return `Your order #${data.orderNumber} has been shipped. Tracking number: ${data.trackingNumber}`;
      case 'product_delivered':
        return `Your order #${data.orderNumber} has been delivered successfully.`;
      case 'qc_approved':
        return `Your order #${data.orderNumber} has passed quality check and is being processed.`;
      case 'qc_rejected':
        return `Your order #${data.orderNumber} requires attention. Please contact us for details.`;
      default:
        return `Update on your order #${data.orderNumber}`;
    }
  };

  // Get order by ID
  const getOrderById = (orderId: string) => {
    return orders.find(order => order.id === orderId);
  };

  // Get orders by status
  const getOrdersByStatus = (status: string) => {
    return orders.filter(order => order.status === status);
  };

  // Get orders in fulfillment pipeline
  const getFulfillmentOrders = () => {
    const fulfillmentStatuses = [
      'confirmed', 
      'under_manufacture', 
      'manufactured', 
      'received_ho', 
      'qc_approved', 
      'qc_rejected',
      'hallmarked', 
      'tagged', 
      'transferred_to_branch', 
      'received_branch'
    ];
    return orders.filter(order => fulfillmentStatuses.includes(order.status));
  };

  return {
    orders,
    isLoading,
    error,
    updateOrderStatus: updateOrderStatus.mutate,
    downloadFinishedProduct: downloadFinishedProduct.mutate,
    transferToBranch: transferToBranch.mutate,
    notifyCustomer: notifyCustomer.mutate,
    getOrderById,
    getOrdersByStatus,
    getFulfillmentOrders,
    
    // Loading states
    isUpdatingStatus: updateOrderStatus.isPending,
    isDownloading: downloadFinishedProduct.isPending,
    isTransferring: transferToBranch.isPending,
    isNotifying: notifyCustomer.isPending
  };
};